// react
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { sp } from '@/theme/mui';

// components
import { Card } from '@/components/atoms/Card/Card';
import { Container } from '@/components/atoms/Container/Container';
import { Icon } from '@/components/atoms/Icon/Icon';
import { Title } from '@/components/atoms/Title/Title';

import { FloatingActions } from '@/components/molecules/FloatingActions/FloatingActions';

interface PoaSuccessProps {
  className?: string;
}

/**
 * PoaSuccess renders the success screen when a user has successfully
 * applied for POA.
 */
export const PoaSuccess = ({ className }: PoaSuccessProps) => {
  return (
    <Container className={className}>
      <Wrapper>
        <Title>Power of Attorney</Title>
        <IconWrapper>
          <Icon icon='check' fontSize={24} />
        </IconWrapper>
        <SubTitle>Payment Successful</SubTitle>
        <Description>
          You have successfully purchased your Power of Attorney through Zenco!
        </Description>
        <Card color='blue' elevation={0} isBordered={false} isPaddingSm>
          <CardTitle>Your next steps</CardTitle>
          <CardUL>
            <li>Click below to return to our app’s Home Screen</li>
            <li>
              Need help? Contact us at{' '}
              <a href={`mailto:${process.env.REACT_APP_SUPPORT_EMAIL}`}>
                {process.env.REACT_APP_SUPPORT_EMAIL}
              </a>
            </li>
          </CardUL>
        </Card>
      </Wrapper>
      <FloatingActions
        actions={[
          {
            label: 'Return Home',
            to: '/',
          },
        ]}
      />
    </Container>
  );
};

const Wrapper = ({ children }: { children?: React.ReactNode }) => (
  <Stack sx={{ alignItems: 'center', gap: sp[16] }}>{children}</Stack>
);
const IconWrapper = ({ children }: { children?: React.ReactNode }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: '99999px',
      width: '40px',
      height: '40px',
      minWidth: '40px',
      minHeight: '40px',
      maxWidth: '40px',
      maxHeight: '40px',
      background: '#335f43',
      color: '#ffffff',
    }}
  >
    {children}
  </Box>
);
const SubTitle = ({ children }: { children?: React.ReactNode }) => (
  <Box sx={{ typography: 'headingSm', color: '#000000' }}>{children}</Box>
);
const Description = ({ children }: { children?: React.ReactNode }) => (
  <Box sx={{ typography: 'bodyMd', color: '#404042' }}>{children}</Box>
);
const CardTitle = ({ children }: { children?: React.ReactNode }) => (
  <Box sx={{ typography: 'bodyLgMedium', color: '#404042' }}>{children}</Box>
);
const CardUL = ({ children }: { children?: React.ReactNode }) => (
  <Box
    component='ul'
    sx={{
      listStyleType: 'disc',
      pl: sp[16],
      '& a': { color: '#f18b6c' },
      '& > li': { pb: sp[16] },
      '& > li:last-child': { pb: 0 },
    }}
  >
    {children}
  </Box>
);

PoaSuccess.displayName = 'PoaSuccess';

export default PoaSuccess;
